import { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import FeedTypeSelector from './FeedTypeSelector'
import FeedUrlInput from './FeedUrlInput'
import FeedPreview from './FeedPreview'
import { api } from '../../../services/api'

const STEPS = [
  { id: 'type', label: 'Select Type' },
  { id: 'url', label: 'Enter URL' },
  { id: 'preview', label: 'Preview' }
]

function FeedImportWizard({ onComplete, onCancel }) {
  const { t } = useTranslation('ecommerce')
  const [currentStep, setCurrentStep] = useState(0)
  const [feedType, setFeedType] = useState('')
  const [feedUrl, setFeedUrl] = useState('')
  const [urlError, setUrlError] = useState(null)
  const [previewData, setPreviewData] = useState({ products: [], totalCount: 0 })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const validateUrl = (url) => {
    if (!url) return false
    try {
      new URL(url)
      return true
    } catch {
      return false
    }
  }

  const canProceed = useCallback(() => {
    switch (currentStep) {
      case 0:
        return !!feedType
      case 1:
        return validateUrl(feedUrl)
      case 2:
        return previewData.totalCount > 0
      default:
        return false
    }
  }, [currentStep, feedType, feedUrl, previewData.totalCount])

  const handleNext = useCallback(async () => {
    if (currentStep === 1) {
      // Validate feed before moving to preview
      setLoading(true)
      setError(null)
      try {
        const result = await api.validateFeed({
          url: feedUrl,
          type: feedType
        })
        if (result.valid) {
          setPreviewData({
            products: result.products || [],
            totalCount: result.totalCount || 0
          })
          setCurrentStep(currentStep + 1)
        } else {
          setUrlError(result.error || t('feed.errors.invalid', 'Invalid feed format'))
        }
      } catch (err) {
        setError(err.message || t('feed.errors.validation', 'Failed to validate feed'))
      } finally {
        setLoading(false)
      }
    } else {
      setCurrentStep(currentStep + 1)
    }
  }, [currentStep, feedUrl, feedType, t])

  const handleBack = useCallback(() => {
    setCurrentStep(currentStep - 1)
    setError(null)
  }, [currentStep])

  const handleImport = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // 1. Create the feed
      const result = await api.createFeed({
        feed_url: feedUrl,
        feed_type: feedType,
        name: t('feed.defaultName', 'Product Feed'),
      })
      // 2. Auto-sync to import products (defensive check for syncFeed function)
      if (result?.feed_id && typeof api.syncFeed === 'function') {
        try {
          await api.syncFeed(result.feed_id)
        } catch (syncErr) {
          // Log sync errors but don't fail the import - feed was created successfully
          console.error('[FeedImportWizard] Auto-sync failed:', syncErr)
        }
      } else if (result?.feed_id) {
        // syncFeed not available, skipping auto-sync
      } else {
        console.error('[FeedImportWizard] No feed_id in result:', result)
      }

      // 3. Complete the wizard (wrap in try-catch to prevent callback errors from breaking UI)
      try {
        onComplete(result)
      } catch (callbackErr) {
        console.error('[FeedImportWizard] onComplete callback failed:', callbackErr)
        // Don't throw - the import itself succeeded
      }
    } catch (err) {
      console.error('[FeedImportWizard] Import failed:', err)
      // Extract error message safely
      const errorMessage = err?.message ||
        (typeof err === 'string' ? err : null) ||
        t('feed.errors.import', 'Failed to import feed')
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [feedUrl, feedType, t, onComplete])

  const handleUrlChange = useCallback((value) => {
    setFeedUrl(value)
    setUrlError(null)
  }, [])

  return (
    <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">
          {t('feed.wizard.title', 'Import Product Feed')}
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          {t('feed.wizard.subtitle', 'Connect your product catalog to create stories')}
        </p>
      </div>

      {/* Step Indicator */}
      <nav aria-label={t('feed.wizard.navigation', 'Wizard steps')} className="px-6 py-3 bg-gray-50 border-b border-gray-200">
        <ol className="flex items-center">
          {STEPS.map((step, index) => (
            <li key={step.id} className="flex items-center">
              <span
                className={`
                  flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium
                  ${index < currentStep
                    ? 'bg-primary-600 text-white'
                    : index === currentStep
                      ? 'bg-primary-100 text-primary-700 ring-2 ring-primary-600'
                      : 'bg-gray-200 text-gray-500'
                  }
                `}
                aria-current={index === currentStep ? 'step' : undefined}
              >
                {index < currentStep ? (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  index + 1
                )}
              </span>
              <span className={`ml-2 text-sm ${index === currentStep ? 'font-medium text-gray-900' : 'text-gray-500'}`}>
                {t(`feed.wizard.steps.${step.id}`, step.label)}
              </span>
              {index < STEPS.length - 1 && (
                <div className="mx-4 w-12 h-0.5 bg-gray-300" aria-hidden="true" />
              )}
            </li>
          ))}
        </ol>
        <p className="mt-2 text-xs text-gray-500">
          {t('feed.wizard.stepIndicator', 'Step {{current}} of {{total}}', {
            current: currentStep + 1,
            total: STEPS.length
          }).replace('{{current}}', currentStep + 1).replace('{{total}}', STEPS.length)}
        </p>
      </nav>

      {/* Step Content */}
      <div className="px-6 py-6 min-h-[300px]">
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {currentStep === 0 && (
          <FeedTypeSelector
            value={feedType}
            onChange={setFeedType}
            disabled={loading}
          />
        )}

        {currentStep === 1 && (
          <FeedUrlInput
            value={feedUrl}
            onChange={handleUrlChange}
            error={urlError}
            disabled={loading}
          />
        )}

        {currentStep === 2 && (
          <FeedPreview
            products={previewData.products}
            totalCount={previewData.totalCount}
            loading={loading}
            error={error}
          />
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
        >
          {t('common.cancel', 'Cancel')}
        </button>
        <div className="flex items-center gap-3">
          {currentStep > 0 && (
            <button
              type="button"
              onClick={handleBack}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              {t('common.back', 'Back')}
            </button>
          )}
          {currentStep < STEPS.length - 1 ? (
            <button
              type="button"
              onClick={handleNext}
              disabled={!canProceed() || loading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  {t('common.validating', 'Validating...')}
                </span>
              ) : (
                t('common.next', 'Next')
              )}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleImport}
              disabled={!canProceed() || loading}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  {t('common.importing', 'Importing...')}
                </span>
              ) : (
                t('feed.wizard.import', 'Import Feed')
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

FeedImportWizard.propTypes = {
  onComplete: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
}

export default FeedImportWizard
